import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { getCatalogues, IMAGE_PATH } from "../../services/api";
import { useNavigate } from "react-router-dom";
import "./InspiredSpaces.css";

/* ---------------- Section Animation (Bottom → Up) ---------------- */
const sectionVariant = {
    hidden: {
        opacity: 0,
        y: 20, // subtle lift (prevents conflict with slider X animation)
    },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.55,
            ease: "easeOut",
        },
    },
};

export default function InspiredSpaces() {
    const [catalogues, setCatalogues] = useState([]);
    const [activeTag, setActiveTag] = useState(null);
    const [index, setIndex] = useState(0);
    const [slideWidth, setSlideWidth] = useState(0);

    const cardRef = useRef(null);
    const navigate = useNavigate();
    const GAP = 40;

    /* ---------------- Fetch Catalogue Data ---------------- */
    useEffect(() => {
        async function fetchData() {
            try {
                const res = await getCatalogues(true);

                // normalize response
                const raw = Array.isArray(res?.data)
                    ? res.data
                    : Array.isArray(res?.data?.data)
                        ? res.data.data
                        : [];

                const formatted = raw.map((c) => ({
                    id: c.id,
                    image: IMAGE_PATH + "catalogue-banners/" + c.bannerImage,
                    tagCount: c.products?.length || 0,
                    looks: (c.products || []).map((p) => {
                        let thumb = "";

                        // ✅ FIX: parse images JSON correctly
                        try {
                            const imgs = JSON.parse(p.images || "[]");
                            if (Array.isArray(imgs) && imgs[0]) {
                                thumb = IMAGE_PATH + imgs[0];
                            }
                        } catch {
                            thumb = "";
                        }

                        return {
                            id: p.id,
                            title: p.productName,
                            price: p.discountcost
                                ? `₹${p.discountcost}`
                                : `₹${p.cost}`,
                            thumb,
                        };
                    }),
                }));

                setCatalogues(formatted);
            } catch (err) {
                console.error("Failed to load catalogues:", err);
            }
        }

        fetchData();
    }, []);

    /* ---------------- Measure Card Width ---------------- */
    useEffect(() => {
        const updateWidth = () => {
            if (cardRef.current) {
                setSlideWidth(cardRef.current.offsetWidth + GAP);
            }
        };
        updateWidth();
        window.addEventListener("resize", updateWidth);
        return () => window.removeEventListener("resize", updateWidth);
    }, []);

    const handleNext = () => {
        if (!catalogues.length) return;
        setActiveTag(null);
        setIndex((prev) => (prev + 1) % catalogues.length);
    };

    const handlePrev = () => {
        if (!catalogues.length) return;
        setActiveTag(null);
        setIndex(
            (prev) => (prev - 1 + catalogues.length) % catalogues.length
        );
    };

    return (
        <motion.section
            className="isp-section"
            variants={sectionVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.3 }}
        >
            <h2 className="isp-heading">Get Inspired by Spaces</h2>

            <div className="isp-slider-container">
                <motion.div
                    className="isp-slider-track"
                    animate={{ x: -index * slideWidth }}
                    transition={{ duration: 0.7, ease: "easeInOut" }}
                >
                    {Array.isArray(catalogues) &&
                        catalogues.map((cat, i) =>  (
                        <div
                            className="isp-card"
                            key={cat.id}
                            ref={i === 0 ? cardRef : null}
                        >
                            <div className="isp-img-wrapper">
                                <img
                                    src={cat.image}
                                    alt=""
                                    className="isp-img"
                                />

                                {/* Tag pill */}
                                <button
                                    className="isp-tag-btn"
                                    onClick={() =>
                                        setActiveTag((prev) =>
                                            prev === cat.id ? null : cat.id
                                        )
                                    }
                                >
                                    <span className="isp-tag-icon">⌁</span>
                                    <span>{cat.tagCount}</span>
                                </button>

                                {/* Modal (NO framer motion here) */}
                                {activeTag === cat.id && (
                                    <div className="isp-modal">
                                        <div className="isp-modal-header">
                                            <span>Shop this look</span>
                                            <button
                                                className="isp-modal-close"
                                                onClick={() =>
                                                    setActiveTag(null)
                                                }
                                            >
                                                ×
                                            </button>
                                        </div>

                                        <div className="isp-modal-body">
                                            {cat.looks.map((look) => (
                                                <div
                                                    key={look.id}
                                                    className="isp-look-item"
                                                    onClick={() =>
                                                        navigate(
                                                            `/product/${look.id}`
                                                        )
                                                    }
                                                >
                                                    <div className="isp-look-thumb-wrap">
                                                        {look.thumb && (
                                                            <img
                                                                src={look.thumb}
                                                                alt={look.title}
                                                                className="isp-look-thumb"
                                                            />
                                                        )}
                                                    </div>

                                                    <div className="isp-look-info">
                                                        <p className="isp-look-title">
                                                            {look.title}
                                                        </p>
                                                        <p className="isp-look-price">
                                                            {look.price}
                                                        </p>
                                                    </div>

                                                    <span className="isp-look-arrow">
                                                        →
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </motion.div>
            </div>

            <div className="isp-bottom-row container">
                <div className="isp-bottom-divider" />
                <div className="isp-bottom-arrows">
                    <button
                        type="button"
                        className="isp-arrow-btn"
                        onClick={handlePrev}
                    >
                        ‹
                    </button>
                    <button
                        type="button"
                        className="isp-arrow-btn"
                        onClick={handleNext}
                    >
                        ›
                    </button>
                </div>
            </div>
        </motion.section>
    );
}
